import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Inbox, Package, Plus, Search, Upload, Download,
  Edit3, ToggleLeft, ToggleRight, RefreshCw, X, FileSpreadsheet,
  AlertTriangle, CheckCircle2, School as SchoolIcon, Store, ShoppingBag, Wallet,
  Settings as SettingsIcon,
} from "lucide-react";

const NAV = [
  { to: "/super/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/super/schools", label: "Schools", icon: SchoolIcon },
  { to: "/super/products", label: "Products", icon: Package },
  { to: "/super/vendors", label: "Vendors", icon: Store },
  { to: "/super/orders", label: "Orders & Ops", icon: ShoppingBag },
  { to: "/super/finance", label: "Finance", icon: Wallet },
  { to: "/super/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/super/settings", label: "Settings", icon: SettingsIcon },
];

const EMPTY_PRODUCT = {
  name: "", category: "", price: "", stock: "100",
  class_level: "All", description: "", image: "",
  active: true, vendor_id: "",
};

function fmtINR(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN")}`;
}

export default function Products() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [editing, setEditing] = useState(null);   // product or null
  const [showBulk, setShowBulk] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (catFilter) params.category = catFilter;
      if (activeFilter) params.active = activeFilter;
      const [{ data }, catRes] = await Promise.all([
        api.get("/admin/products", { params }),
        categories.length ? Promise.resolve({ data: categories }) : api.get("/categories"),
      ]);
      setRows(data);
      if (!categories.length) setCategories(catRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const stats = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((r) => r.active !== false).length;
    const cats = new Set(rows.map((r) => r.category)).size;
    return { total, active, inactive: total - active, cats };
  }, [rows]);

  const applyFilters = () => load();

  const toggleActive = async (r) => {
    try {
      const { data } = await api.patch(`/admin/products/${r.id}`, { active: !r.active });
      setRows((s) => s.map((x) => (x.id === r.id ? { ...x, ...data } : x)));
      toast.success(data.active ? "Product activated" : "Product deactivated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const save = async (form) => {
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        price: Number(form.price || 0),
        stock: parseInt(form.stock || 0, 10),
        class_level: form.class_level || "All",
        description: form.description || "",
        image: form.image || "",
        active: !!form.active,
        vendor_id: form.vendor_id || null,
      };
      if (form.id) {
        const { data } = await api.patch(`/admin/products/${form.id}`, payload);
        setRows((s) => s.map((x) => (x.id === form.id ? { ...x, ...data } : x)));
        toast.success("Product updated");
      } else {
        const { data } = await api.post("/admin/products", payload);
        setRows((s) => [{ ...data, school_name: "Global catalog" }, ...s]);
        toast.success("Product created");
      }
      setEditing(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <DashboardLayout nav={NAV} title="Product Management">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="text-2xl font-heading font-semibold text-brand-navy" data-testid="products-title">
            Product Catalog
          </div>
          <div className="text-sm text-slate-500">
            {stats.total} total · {stats.active} active · {stats.cats} {stats.cats === 1 ? "category" : "categories"}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowBulk(true)}
            data-testid="products-bulk-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 bg-white text-sm hover:border-slate-400"
          >
            <Upload size={14} /> Bulk Import
          </button>
          <button
            onClick={() => setEditing({ ...EMPTY_PRODUCT })}
            data-testid="products-add-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blueDark"
          >
            <Plus size={14} /> Add Product
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="sl-card p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="products-search"
            placeholder="Search name, category, description…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyFilters()}
            className="pl-9 pr-4 py-2 w-full rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-brand-blue"
          />
        </div>
        <select
          data-testid="products-category-filter"
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
        >
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          data-testid="products-active-filter"
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value)}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
        >
          <option value="">Any status</option>
          <option value="true">Active only</option>
          <option value="false">Inactive only</option>
        </select>
        <button
          onClick={applyFilters}
          data-testid="products-apply-filters"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-700"
        >
          Apply
        </button>
        <button
          onClick={() => { setQ(""); setCatFilter(""); setActiveFilter(""); setTimeout(load, 0); }}
          data-testid="products-clear-filters"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-slate-300 text-sm hover:border-slate-400"
        >
          <RefreshCw size={13} /> Clear
        </button>
      </div>

      {/* Table */}
      <div className="sl-card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3">Product</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Class</th>
              <th className="text-right px-4 py-3">Price</th>
              <th className="text-right px-4 py-3">Stock</th>
              <th className="text-left px-4 py-3">Tenant</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center py-10 text-slate-500">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-slate-500" data-testid="products-empty">
                No products {q || catFilter || activeFilter ? "match your filters" : "yet — add your first one"}.
              </td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`product-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {r.image ? (
                      <img src={r.image} alt="" className="w-10 h-10 rounded object-cover border border-slate-200"
                           onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    ) : (
                      <div className="w-10 h-10 rounded bg-slate-100 grid place-items-center text-slate-400">
                        <Package size={16} />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{r.name}</div>
                      {r.description && <div className="text-xs text-slate-500 truncate max-w-xs">{r.description}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">{r.category}</td>
                <td className="px-4 py-3 text-slate-600">{r.class_level || "All"}</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-800">{fmtINR(r.price)}</td>
                <td className="px-4 py-3 text-right text-slate-600">{r.stock}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{r.school_name || "Global catalog"}</td>
                <td className="px-4 py-3 text-center">
                  {r.active !== false ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-brand-greenSoft text-brand-greenDark text-xs font-semibold">
                      <CheckCircle2 size={12} /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">
                      Inactive
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={() => setEditing({ ...r, price: String(r.price), stock: String(r.stock) })}
                      data-testid={`product-edit-${r.id}`}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                      title="Edit"
                    >
                      <Edit3 size={15} />
                    </button>
                    <button
                      onClick={() => toggleActive(r)}
                      data-testid={`product-toggle-${r.id}`}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                      title={r.active !== false ? "Deactivate" : "Activate"}
                    >
                      {r.active !== false ? <ToggleRight size={17} className="text-brand-green" /> : <ToggleLeft size={17} className="text-slate-400" />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ProductEditor
          initial={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
      {showBulk && (
        <BulkImportDialog
          onClose={() => setShowBulk(false)}
          onCommitted={() => { setShowBulk(false); load(); }}
        />
      )}
    </DashboardLayout>
  );
}

/* ------------- Editor ------------- */
function ProductEditor({ initial, categories, onClose, onSave }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(f.id);
  const set = (k) => (e) => setF((s) => ({
    ...s,
    [k]: e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e,
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Product name is required");
    if (!f.category) return toast.error("Please choose a category");
    if (Number(f.price) < 0) return toast.error("Price must be ≥ 0");
    setSaving(true);
    await onSave(f);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="product-editor">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <form onSubmit={submit} className="w-full max-w-xl bg-white border-l border-slate-200 shadow-xl overflow-y-auto">
        <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-brand-greenDark font-semibold">
              {isEdit ? "Edit product" : "Add product"}
            </div>
            <h3 className="mt-1 text-lg font-heading font-semibold text-brand-navy">
              {isEdit ? f.name : "New catalog item"}
            </h3>
          </div>
          <button type="button" onClick={onClose} data-testid="product-editor-close"
                  className="text-slate-400 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Name *</label>
            <input required data-testid="product-name"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-brand-blue"
              value={f.name} onChange={set("name")} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Category *</label>
              <select required data-testid="product-category" value={f.category} onChange={set("category")}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 bg-white">
                <option value="">Choose category…</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Class level</label>
              <input data-testid="product-class" value={f.class_level} onChange={set("class_level")}
                placeholder="All, or 1-12"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Price (₹) *</label>
              <input required type="number" step="0.01" min="0" data-testid="product-price"
                value={f.price} onChange={set("price")}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Stock</label>
              <input type="number" min="0" step="1" data-testid="product-stock"
                value={f.stock} onChange={set("stock")}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Image URL</label>
            <input data-testid="product-image" value={f.image} onChange={set("image")}
              placeholder="https://…"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
            {f.image && (
              <img src={f.image} alt="preview" className="mt-2 h-24 rounded object-cover border border-slate-200"
                   onError={(e) => { e.currentTarget.style.display = "none"; }} />
            )}
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Description</label>
            <textarea rows={3} data-testid="product-description"
              value={f.description} onChange={set("description")}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
          </div>
          <label className="inline-flex items-center gap-2 text-slate-700">
            <input type="checkbox" data-testid="product-active" checked={!!f.active} onChange={set("active")}
              className="rounded border-slate-300" />
            <span>Active (visible to schools)</span>
          </label>
        </div>
        <div className="p-6 border-t border-slate-100 flex items-center gap-3">
          <button type="submit" disabled={saving} data-testid="product-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand-blue text-white font-semibold hover:bg-brand-blueDark disabled:opacity-60">
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Create product")}
          </button>
          <button type="button" onClick={onClose}
            className="px-5 py-2.5 rounded-md border border-slate-300 hover:border-slate-400">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

/* ------------- Bulk import dialog ------------- */
function BulkImportDialog({ onClose, onCommitted }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);   // dry-run result
  const [committing, setCommitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const dryRun = async (chosen) => {
    const fd = new FormData();
    fd.append("file", chosen);
    fd.append("dry_run", "true");
    try {
      const { data } = await api.post("/admin/products/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onFile = async (e) => {
    const chosen = e.target.files?.[0];
    if (!chosen) return;
    setFile(chosen); setPreview(null);
    await dryRun(chosen);
  };

  const onDrop = async (e) => {
    e.preventDefault(); setDragActive(false);
    const chosen = e.dataTransfer.files?.[0];
    if (!chosen) return;
    setFile(chosen); setPreview(null);
    await dryRun(chosen);
  };

  const commit = async () => {
    if (!file) return;
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", "false");
      const { data } = await api.post("/admin/products/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.committed} products imported${data.error_rows ? ` (${data.error_rows} skipped)` : ""}`);
      onCommitted();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="bulk-dialog">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white border-l border-slate-200 shadow-xl overflow-y-auto">
        <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-brand-greenDark font-semibold">
              Bulk import
            </div>
            <h3 className="mt-1 text-lg font-heading font-semibold text-brand-navy">
              Upload a CSV or Excel catalog
            </h3>
            <p className="text-xs text-slate-500 mt-1 max-w-md">
              Not sure of the format? Download the template, fill it in, and drop it back here.
              Nothing is imported until you confirm.
            </p>
          </div>
          <button onClick={onClose} data-testid="bulk-close"
                  className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-5 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={`${API_BASE}/admin/products/template.xlsx`}
              data-testid="bulk-template-xlsx"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 bg-white text-sm hover:border-slate-400"
            >
              <FileSpreadsheet size={14} /> Download XLSX template
            </a>
            <a
              href={`${API_BASE}/admin/products/template.csv`}
              data-testid="bulk-template-csv"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 bg-white text-sm hover:border-slate-400"
            >
              <Download size={14} /> Download CSV template
            </a>
          </div>

          <label
            onDrop={onDrop}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            className={`block rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
              dragActive ? "border-brand-blue bg-brand-blueSoft" : "border-slate-300 bg-slate-50 hover:border-slate-400"
            }`}
            data-testid="bulk-dropzone"
          >
            <Upload size={28} className="mx-auto text-slate-500" />
            <div className="mt-3 text-sm font-semibold text-slate-800">
              Drop your CSV or XLSX file here
            </div>
            <div className="text-xs text-slate-500 mt-1">or click to browse (max 5 MB, 5000 rows)</div>
            <input
              type="file"
              accept=".csv,.xlsx"
              onChange={onFile}
              className="hidden"
              data-testid="bulk-file-input"
            />
            {file && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs text-slate-700">
                <FileSpreadsheet size={14} /> {file.name}
              </div>
            )}
          </label>

          {preview && (
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="bulk-preview">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-2xl font-heading font-bold text-brand-navy" data-testid="bulk-total">{preview.total_rows}</div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-1">Rows found</div>
                </div>
                <div className="rounded-lg bg-brand-greenSoft p-3">
                  <div className="text-2xl font-heading font-bold text-brand-greenDark" data-testid="bulk-valid">{preview.valid_rows}</div>
                  <div className="text-[11px] uppercase tracking-wide text-brand-greenDark mt-1">Valid</div>
                </div>
                <div className={`rounded-lg p-3 ${preview.error_rows ? "bg-amber-50" : "bg-slate-50"}`}>
                  <div className={`text-2xl font-heading font-bold ${preview.error_rows ? "text-amber-700" : "text-slate-500"}`}
                       data-testid="bulk-errors">
                    {preview.error_rows}
                  </div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-1">Errors</div>
                </div>
              </div>
              {preview.errors?.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <AlertTriangle size={14} /> Rows that will be skipped
                  </div>
                  <div className="max-h-52 overflow-y-auto border border-amber-100 rounded-lg divide-y divide-amber-100">
                    {preview.errors.slice(0, 50).map((e, i) => (
                      <div key={i} className="p-2 text-xs">
                        <span className="font-semibold text-amber-800">Row {e.row}</span>{" "}
                        <span className="text-slate-700">— {e.error}</span>
                        {e.raw?.name && <span className="text-slate-500"> · "{e.raw.name}"</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-5 flex items-center gap-3">
                <button
                  onClick={commit}
                  disabled={committing || preview.valid_rows === 0}
                  data-testid="bulk-commit"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand-blue text-white font-semibold hover:bg-brand-blueDark disabled:opacity-60"
                >
                  {committing ? "Importing…" : `Import ${preview.valid_rows} products`}
                </button>
                <button
                  onClick={onClose}
                  className="px-5 py-2.5 rounded-md border border-slate-300 hover:border-slate-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
